import { useCallback, useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash-es/isEqual';
import { PlusIcon } from 'lucide-react';
import { FieldValues, Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ToolkitEditorTabs } from '@/components/organisms/Toolkit/ToolkitEditorTabs/ToolkitEditorTabs';
import { Button } from '@/components/ui/button';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import useDialogAlert from '@/hooks/useDialogAlert';
import { generateOption } from '@/redux/dialogs/helper';
import {
  selectActionData,
  selectNodeIdByActionId,
  selectPassthrough,
} from '@/redux/dialogs/selectors';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { updateAction, updateOptions } from '@/redux/nodes/actions';
import { MAX_TEXT_ACTION_OPTIONS } from '@/util/constants';
import { textActionSchema } from '@/util/validation/schemas/action-schemas';

import ToolkitButton from './ToolkitButton';
import Passthrough from '../../Passthrough';
import ToolkitWrapper from '../../ToolkitWrapper';

type FormType = { actionTexts: { textValue: string }[] } & FieldValues;

/**
 * ToolkitActionText Component
 *
 * Handles text action configuration including:
 * - Rich text editing with multiple tabs
 * - Quick options management
 * - Passthrough data configuration for different channels
 *
 * The passthrough functionality is handled by the reusable Passthrough component
 * which allows setting channel-specific payload data that gets sent along with
 * the text action. Changes are debounced and automatically saved to Redux.
 *
 * @returns JSX.Element - The complete text action toolkit interface
 */
const ToolkiActionText = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actionId, texts, options } = useSelector(selectActionData, shallowEqual);
  const passthrough = useSelector(selectPassthrough);
  const errorRefs = useRef<Record<number, boolean>>({});
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const { passthrough: isPassthroughEnabled } = useSelector(selectFeatureFlags);
  const { variables } = useBrainVariables();

  const [optionList, setOptionList] = useState(options);
  const optionListRef = useRef(optionList);

  // Sync local state with Redux when options change (e.g., from canvas OptionList)
  useEffect(() => {
    setOptionList(options);
  }, [options]);

  // Keep ref in sync with local state
  useEffect(() => {
    optionListRef.current = optionList;
  }, [optionList]);

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: { actionTexts: [{ textValue: '' }] },
    resolver: yupResolver(textActionSchema) as Resolver<FormType>,
  });

  // Initialize form with markdown texts
  useEffect(() => {
    setValue(
      'actionTexts',
      texts.map((text) => ({ textValue: text }))
    );
    // this useEffect should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const debouncedUpdateOptions = useDebouncedCallback(
    () => {
      dispatch(updateOptions({ actionId, options: optionListRef.current }));
    },
    200,
    [dispatch, actionId]
  );

  const onAddOptionClick = useCallback(() => {
    const newList = [...optionListRef.current, generateOption()];
    setOptionList(newList);
    debouncedUpdateOptions.cancel();
    dispatch(updateOptions({ actionId, options: newList }));
  }, [dispatch, actionId, debouncedUpdateOptions]);

  const onOptionChange = useCallback(
    (option, index) => {
      const newList = [...optionListRef.current];
      newList[index] = { ...newList[index], ...option };
      setOptionList(newList);
      debouncedUpdateOptions();
    },
    [debouncedUpdateOptions]
  );

  const onOptionDelete = useCallback(
    (index) => {
      const newList = [...optionListRef.current];
      newList.splice(index, 1);
      const options = newList.length > 0 ? newList : undefined;
      setOptionList(newList);
      debouncedUpdateOptions.cancel();
      dispatch(updateOptions({ actionId, options }));
    },
    [dispatch, actionId, debouncedUpdateOptions]
  );

  // Called during drag operations to update the visual order of the options
  const handleDragItem = useCallback(
    (dragIndex, hoverIndex) => {
      const newList = [...optionListRef.current];
      const [draggedItem] = newList.splice(dragIndex, 1);
      newList.splice(hoverIndex, 0, draggedItem);
      setOptionList(newList);
      debouncedUpdateOptions.cancel();
      dispatch(updateOptions({ actionId, options: newList }));
    },
    [dispatch, actionId, debouncedUpdateOptions]
  );

  // Called when a drag operation is completed to update the Redux state
  const handleOnDrop = useCallback(() => {
    debouncedUpdateOptions.cancel();
    dispatch(updateOptions({ actionId, options: optionListRef.current }));
  }, [dispatch, actionId, debouncedUpdateOptions]);

  const getIndex = useCallback((optionId: string) => {
    return optionListRef.current.findIndex((option) => option.option_id === optionId);
  }, []);

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.text'),
    type: 'text',
  });

  useEffect(() => {
    const checkErrors = async () => {
      const watchedTexts = getValues('actionTexts');

      // Check all tabs dynamically
      for (let i = 0; i < watchedTexts.length; i++) {
        const isValid = await trigger(`actionTexts.${i}.textValue`);
        const errorMessage = errors.actionTexts?.[i]?.textValue?.message;
        const tabKey = `tab-${i + 1}`;

        if (!isValid && errorMessage && !errorRefs.current[i]) {
          setFieldError(tabKey, errorMessage);
          errorRefs.current[i] = true;
        } else if (isValid && errorRefs.current[i]) {
          setFieldError(tabKey, '');
          errorRefs.current[i] = false;
        }
      }
    };

    checkErrors();
  }, [trigger, errors.actionTexts, setFieldError, getValues]);

  const handleTabOnChange = useCallback(() => {
    const watchedTexts = getValues('actionTexts');
    const markdownTexts = watchedTexts.map((text) => text.textValue);

    if (!isEqual(markdownTexts, texts)) {
      dispatch(updateAction({ actionId, action: { texts: markdownTexts } }));
    }
  }, [actionId, dispatch, texts, getValues]);

  const debouncedHandleTabOnChange = useDebouncedCallback(handleTabOnChange, 200, [
    handleTabOnChange,
  ]);

  const handleEditorChange = useCallback(
    (index: number, content: string) => {
      setValue(`actionTexts.${index}.textValue`, content, { shouldValidate: true });
      debouncedHandleTabOnChange();
    },
    [setValue, debouncedHandleTabOnChange]
  );

  const handleAddVariant = useCallback(() => {
    const watchedTexts = getValues('actionTexts');
    const markdownTexts = watchedTexts.map((text) => text.textValue);
    const randomSuffix = Math.random().toString(36).substring(2, 6);
    const defaultText = `Example text ${randomSuffix}`;
    const newTexts = [...markdownTexts, defaultText];

    // Update form state
    setValue('actionTexts', [...watchedTexts, { textValue: defaultText }]);

    // Update Redux
    dispatch(updateAction({ actionId, action: { texts: newTexts } }));
  }, [actionId, dispatch, setValue, getValues]);

  const handleDeleteVariant = useCallback(
    (index: number) => {
      const watchedTexts = getValues('actionTexts');
      const markdownTexts = watchedTexts.map((text) => text.textValue);
      const newTexts = markdownTexts.filter((_, i) => i !== index);

      // Update form state
      const newActionTexts = watchedTexts.filter((_, i) => i !== index);
      setValue('actionTexts', newActionTexts);

      // Update Redux
      dispatch(updateAction({ actionId, action: { texts: newTexts } }));
    },
    [actionId, dispatch, setValue, getValues]
  );

  const watchedTexts = useWatch({ control, name: 'actionTexts' });
  const editorValues = watchedTexts.map((text) => text.textValue);
  const editorErrors = watchedTexts.map((_, index) => errors.actionTexts?.[index] || {});

  return (
    <ToolkitWrapper type="text">
      <ToolkitEditorTabs
        values={editorValues}
        onChange={handleEditorChange}
        variableOptions={variables}
        errors={editorErrors}
        onDeleteVariant={handleDeleteVariant}
        onAddVariant={editorValues.length < 3 ? handleAddVariant : undefined}
      />

      <div className="mt-4 mb-4 flex w-full justify-end">
        <Button
          size="sm"
          variant="outline-neutral"
          onClick={onAddOptionClick}
          disabled={optionList.length === MAX_TEXT_ACTION_OPTIONS}
          className="w-full"
        >
          <PlusIcon className="size-4" />
          {t('common.add_quick_option')}
        </Button>
      </div>
      <div className={`flex flex-col gap-4 ${optionList.length > 0 ? 'mb-4' : ''}`}>
        {optionList?.map((o, i) => (
          <ToolkitButton
            key={o.option_id}
            actionId={actionId}
            option={o}
            onChange={onOptionChange}
            onDelete={onOptionDelete}
            index={i}
            handleDragItem={handleDragItem}
            getIndex={getIndex}
            onDrop={handleOnDrop}
          />
        ))}
      </div>
      {isPassthroughEnabled && <Passthrough actionId={actionId} passthrough={passthrough} />}
    </ToolkitWrapper>
  );
};

export default ToolkiActionText;
